'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/app/utils';

const COLUMNS = ['referenceToVideo', 'imageToVideo'] as const;

const ROWS = ['coreUseCase', 'inputType', 'controlLevel', 'consistency', 'bestFor', 'output', 'complexity'] as const;

const COLUMN_COLORS = ['#AC8AFF', '#5AE4D0'] as const;

type ColumnKey = (typeof COLUMNS)[number];
type RowKey = (typeof ROWS)[number];

/** 正向徽章（绿色）坐标 */
const POSITIVE_CELLS: Partial<Record<RowKey, readonly ColumnKey[]>> = {
  consistency: ['referenceToVideo'],
};

/** 负向徽章（红色）坐标 */
const NEGATIVE_CELLS: Partial<Record<RowKey, readonly ColumnKey[]>> = {
  consistency: ['imageToVideo'],
};

function BadgeSuccess({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center justify-center whitespace-normal rounded-full bg-[rgba(61,160,112,0.3)] px-3 py-1 font-semibold text-[#3FF99F] text-[11px] leading-[1.5]">
      {children}
    </span>
  );
}

function BadgeDanger({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center justify-center whitespace-normal rounded-full bg-[rgba(147,0,10,0.2)] px-3 py-1 font-semibold text-[#FFDAD6] text-[11px] leading-[1.5]">
      {children}
    </span>
  );
}

export function ComparisonSection() {
  const t = useTranslations('ai-reference-to-video.comparison');

  return (
    <section className="pt-16 sm:pt-8">
      <div className="mx-auto flex flex-col items-center px-4">
        <div className="mb-12 flex w-[74vw] flex-col items-center gap-2 text-center sm:mb-8 sm:w-full">
          <h2 className="font-semibold text-[34px] text-white leading-[1.2] sm:text-[28px]">{t('title')}</h2>
          <p className="text-base text-white/65 leading-[1.6]">{t('description')}</p>
        </div>

        <div className="w-[74vw] overflow-x-auto rounded-lg shadow-2xl sm:w-full">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-white/40 border-b">
                <th className="h-20 w-[240px] min-w-[180px] px-6 text-left align-middle font-medium text-sm text-white uppercase tracking-[0.7px] sm:w-[140px] sm:min-w-[140px] sm:px-4 sm:text-xs">
                  {t('columns.dimension')}
                </th>
                {COLUMNS.map((col, i) => (
                  <th
                    key={col}
                    className={cn(
                      'relative h-20 min-w-[160px] border-white/[0.16] border-l px-1 text-center align-middle sm:min-w-[120px]',
                      col === 'referenceToVideo' && 'bg-white/[0.08]',
                    )}
                  >
                    <span className="font-semibold text-base text-white">{t(`columns.${col}`)}</span>
                    <div
                      className="absolute inset-x-0 bottom-0 h-0.5"
                      style={{
                        backgroundColor: COLUMN_COLORS[i],
                        boxShadow: `0 0 15px 0 ${COLUMN_COLORS[i]}80`,
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row} className="border-white/[0.16] border-b">
                  <td className="h-16 px-6 align-middle font-semibold text-sm text-white sm:px-4 sm:text-xs">
                    {t(`rows.${row}.dimension`)}
                  </td>
                  {COLUMNS.map((col) => {
                    const label = t(`rows.${row}.${col}`);
                    const isPositive = POSITIVE_CELLS[row]?.includes(col);
                    const isNegative = NEGATIVE_CELLS[row]?.includes(col);
                    return (
                      <td
                        key={col}
                        className={cn(
                          'h-16 border-white/[0.16] border-l px-4 text-center align-middle text-sm text-white sm:px-3',
                          col === 'referenceToVideo' && 'bg-white/[0.08]',
                        )}
                      >
                        {isPositive ? (
                          <BadgeSuccess>{label}</BadgeSuccess>
                        ) : isNegative ? (
                          <BadgeDanger>{label}</BadgeDanger>
                        ) : (
                          label
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
